import { useContext, useLayoutEffect } from 'react';

import {
  DetailOverlayContext,
  EMPTY_SLOTS,
  type DetailOverlayContextValue,
  type DetailOverlaySlots,
} from './detailOverlay.types';

function useDetailOverlayContext(): DetailOverlayContextValue {
  const context = useContext(DetailOverlayContext);
  if (!context) {
    throw new Error(
      'useDetailOverlayContext must be used within DetailOverlayProvider'
    );
  }
  return context;
}

/**
 * Publish detail-panel slots to the unified overlay shell. Registration is
 * synchronous (useLayoutEffect) so the shell renders with correct slots
 * in the same paint as the content mount — avoids a flash of empty header.
 *
 * Dependencies deliberately destructure each slot field so callers can
 * pass a fresh object literal every render without triggering setSlots
 * loops. Only changes to a field's value cause a publish.
 */
export function useRegisterDetailSlots(slots: DetailOverlaySlots): void {
  const { setSlots } = useDetailOverlayContext();
  const {
    headerContent,
    onEdit,
    onDelete,
    fullscreenWideContent,
    defaultFullscreen,
    previewMode,
    onPreviewModeChange,
  } = slots;

  useLayoutEffect(() => {
    setSlots({
      headerContent,
      onEdit,
      onDelete,
      fullscreenWideContent,
      defaultFullscreen,
      previewMode,
      onPreviewModeChange,
    });
    return () => {
      setSlots(EMPTY_SLOTS);
    };
  }, [
    setSlots,
    headerContent,
    onEdit,
    onDelete,
    fullscreenWideContent,
    defaultFullscreen,
    previewMode,
    onPreviewModeChange,
  ]);
}

/**
 * Read + write access to the shared fullscreen flag. Consumed by content
 * components (to drive sidebar layout) and by the SlidePanel shell (to
 * wire `onFullscreenChange`).
 */
export function useDetailOverlayFullscreen(): {
  isFullscreenMode: boolean;
  setIsFullscreenMode: (next: boolean) => void;
} {
  const { isFullscreenMode, setIsFullscreenMode } = useDetailOverlayContext();
  return { isFullscreenMode, setIsFullscreenMode };
}

/**
 * Read-only access to the currently published slots. Used by the SlidePanel
 * shell to forward slots into its props.
 */
export function useDetailOverlaySlots(): DetailOverlaySlots {
  return useDetailOverlayContext().slots;
}
